/**
 * Reusable Markdown Renderer
 * Used across chat messages, canvas preview, and other markdown content
 */

import type * as React from "react"
import { useMemo } from "react"

import ReactMarkdown, { defaultUrlTransform } from "react-markdown"
import rehypeKatex from "rehype-katex"
import rehypeRaw from "rehype-raw"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"
import "katex/dist/katex.min.css"

import type { Components, UrlTransform } from "react-markdown"

import { CodeBlock } from "@/features/chat/components/code-block"
import { InlineSessionImage } from "@/features/chat/components/inline-session-image"
import styles from "@/features/chat/styles/answer.module.css"
import { cn } from "@/lib/utils"

/** Allow cite: protocol for citation links; delegate everything else to the default sanitizer. */
function defaultCiteUrlTransform(url: string): string {
  if (url.startsWith("cite:")) return url
  return defaultUrlTransform(url)
}

type SessionImageContext = {
  sessionId: string
  filenames: string[]
  /** When set (saved artifact), fetch images from this document-content base
   * instead of /session-content, so shared recipients can load them. */
  contentBaseUrl?: string
}

interface MarkdownRendererProps {
  content: string
  className?: string
  components?: Partial<Components>
  sessionImageContext?: SessionImageContext
  isStreaming?: boolean
  urlTransform?: UrlTransform
}

export function MarkdownRenderer({
  content,
  className,
  components,
  sessionImageContext,
  isStreaming = false,
  urlTransform,
}: MarkdownRendererProps) {
  const mergedComponents = useMemo(() => {
    const baseComponents: Partial<Components> = {
      code: (props: { inline?: boolean; className?: string; children?: React.ReactNode }) => (
        <CodeBlock {...props} isStreaming={isStreaming} />
      ),
    }

    if (sessionImageContext?.sessionId || sessionImageContext?.contentBaseUrl) {
      const ctx = sessionImageContext
      baseComponents.img = ({ src, alt, ...props }: { src?: string; alt?: string }) => (
        <InlineSessionImage
          key={`${ctx.contentBaseUrl ?? ctx.sessionId}::${src}`}
          alt={alt}
          contentBaseUrl={ctx.contentBaseUrl}
          filenames={ctx.filenames}
          isStreaming={isStreaming}
          sessionId={ctx.sessionId}
          src={src}
          {...props}
        />
      )
    }

    return {
      ...baseComponents,
      ...components,
    }
  }, [sessionImageContext, components, isStreaming])

  return (
    <div className={cn(styles.answerText, "prose dark:prose-invert", className)}>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw, rehypeKatex]}
        remarkPlugins={[remarkGfm, remarkMath]}
        urlTransform={urlTransform ?? defaultCiteUrlTransform}
        components={
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          mergedComponents as any
        }
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
